import { config } from '@config';
import { BehaviorSubject, of } from 'rxjs';
import { concatMap, tap, map, catchError } from 'rxjs/operators';
import { RxUtils, TypeUtils } from '@utils';
import { httpService } from '@services/http';
import { logger } from '@services/logger';
import { ElectrificationWizard } from './electrification-wizard.class';

type Entity = ElectrificationWizard;

export class ElectrificationWizardService {
	private cache$: BehaviorSubject<Maybe<Entity>>;

	private getUrl() {
		return `${config.apiUrl}/electrification-wizard`;
	}

	getCachedSimulation() {
		this.cache$ = this.cache$ || new BehaviorSubject<Maybe<Entity>>(undefined);
		if (!this.cache$.getValue()) {
			return this.getLatest().pipe(
				catchError(() => of(undefined)),
				concatMap((entities) => (this.cache$.next(entities), this.cache$))
			);
		}
		return this.cache$;
	}

	create(simulationTaskIds: string[]) {
		return httpService.post<Entity>(this.getUrl(), '', { body: simulationTaskIds, errorHandler: 'notification' }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.create.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(ElectrificationWizard, result))
		);
	}

	getLatest() {
		return httpService.get<Entity[]>(this.getUrl(), 'latest/one').pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getLatest.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(ElectrificationWizard, result)?.[0]),
			map((result) => (this.cache$?.next(result), result))
		);
	}

	get(id: string) {
		return httpService.get<Entity[]>(this.getUrl(), id).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.get.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(ElectrificationWizard, result))
		);
	}

	getAll() {
		return httpService.get<Entity[]>(this.getUrl()).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getAll.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(ElectrificationWizard, result))
		);
	}

	selectPackage(wizardId: string, selectedPackageDealCalculatedId: string) {
		return httpService
			.patch<Entity>(this.getUrl(), `${wizardId}/selected-package`, {
				body: { selectedPackageDealCalculatedId },
				errorHandler: 'notification'
			})
			.pipe(
				tap((result) => logger.debug(`[${this.constructor.name}.${this.selectPackage.name}]`, result)),
				concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
				map((result) => TypeUtils.convert(ElectrificationWizard, result))
			);
	}
}

export const electrificationWizardService = new ElectrificationWizardService();
